import React, { useEffect, useState } from 'react';
import './css/Toast.css';
import DOMPurify from 'dompurify';

export type ToastType = 'info' | 'success' | 'warning' | 'error';


export interface ToastProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string; // and could be html for strong tags
  type?: ToastType;
  autoClose?: boolean;
  duration?: number;
  showIcon?: boolean;
}

// SVG icons for each toast type
const icons = {
    info: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
      </svg>
    ),
    success: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
    ),
    warning: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
      </svg>
    ),
    error: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
      </svg>
    )
  };

const Toast: React.FC<ToastProps> = ({
  isOpen,
  onClose,
  title= "Info",
  message,
  type = 'info',
  autoClose = true,
  duration = 10000,
  showIcon = true,
}) => {
  const [show, setShow] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  
  // Handle close with animation
  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setShow(false);
      setIsClosing(false);
      onClose();
    }, 300); // Match the animation duration
  };
  
  // Update when isOpen changes
  useEffect(() => {
    if (isOpen) {
      setShow(true);
      setIsClosing(false);
    }
  }, [isOpen]);
  
  // Auto-close timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (show && autoClose) {
      timer = setTimeout(handleClose, duration);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, autoClose, duration]);
  
  if (!show) return null;
  
  return (
    <div className="simple-toast-container">
      <div className={`simple-toast toast-${type} ${isClosing ? 'toast-closing' : ''}`}>
        <div className={`simple-toast-header toast-header-${type}`}>
            {showIcon && <span className="toast-icon">{icons[type]}</span>}
            <span className="toast-title">{title}</span>
            {/* <button className="toast-close" onClick={handleClose}>×</button> */}
        </div>
        <div className="simple-toast-body">
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }} />
        </div>
      </div>
    </div>
  );
};

export default Toast;